import { useMemo, useSyncExternalStore } from "react";

/**
 * Reproduces the server's text line-spacing base so the editor preview matches
 * the rendered video.
 *
 * The renderer (Libertyfi.api, `rendering/graphics/composed_rich_text.py`)
 * advances each line by `font_height * (1 + line_spacing)`, where `font_height`
 * is the tight glyph ink bounding box (PIL `font.getbbox`), NOT the em /
 * font-size. CSS `line-height` is always relative to the em, so for the same
 * `lineHeight` value the editor spaces lines wider than the video.
 *
 * We measure the same ink box in the browser via Canvas `measureText`
 * (`actualBoundingBoxAscent + actualBoundingBoxDescent` ≈ PIL `getbbox` height),
 * take the max across lines (as the renderer does), and return an explicit px
 * line-height = `inkHeight * (1 + lineHeight)`.
 *
 * Returns `null` until the font is actually available (an unloaded font yields
 * fallback metrics), so callers fall back to the em-based `1 + lineHeight`.
 */

// A single offscreen canvas reused for all measurements.
let measureCanvas: HTMLCanvasElement | null = null;
const getMeasureContext = (): CanvasRenderingContext2D | null => {
  if (typeof document === "undefined") return null;
  measureCanvas ??= document.createElement("canvas");
  return measureCanvas.getContext("2d");
};

// Subscribe to webfont loading so measurements re-run once fonts are ready.
// Defensive about partial FontFaceSet implementations (e.g. jsdom in tests).
const subscribeToFonts = (onChange: () => void): (() => void) => {
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  if (!fonts) return () => undefined;
  let active = true;
  fonts.ready
    .then(() => {
      if (active) onChange();
    })
    .catch(() => {
      // ignore error
    });
  fonts.addEventListener("loadingdone", onChange);
  return () => {
    active = false;
    fonts.removeEventListener("loadingdone", onChange);
  };
};
// Snapshot changes whenever a new font finishes loading (size) or the loading
// status flips, forcing a re-measure.
const getFontsSnapshot = (): string => {
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  return fonts ? `${fonts.status}:${fonts.size}` : "none";
};

/**
 * Returns a value that changes whenever a webfont finishes loading. Subscribe to
 * it from effects/memos that measure text so they re-run once fonts are ready.
 */
export const useFontsLoadedSignal = (): string =>
  useSyncExternalStore(subscribeToFonts, getFontsSnapshot, () => "none");

type UseTextLineHeightPxArgs = {
  fontFamily: string;
  fontSizePx: number;
  lineHeight: number;
  text: string;
  bold?: boolean;
  italic?: boolean;
  upperCase?: boolean;
};

export type TextLineMetrics = {
  /** Line-height in px, based on the glyph ink box (matches the renderer). */
  lineHeightPx: number;
  /**
   * Distance (px) from the first line's CSS line-box top down to the actual
   * glyph ink-top. This is the empty space CSS leaves above the text that the
   * renderer doesn't (line-box half-leading + the font-ascent-vs-ink-ascent
   * gap). Top-aligned text offsets up by this so the first glyph sits at the
   * box top, like the video.
   */
  topInsetPx: number;
  /** Same for the last line's ink-bottom up to the line-box bottom (for bottom align). */
  bottomInsetPx: number;
};

export const useTextLineHeightPx = ({
  fontFamily,
  fontSizePx,
  lineHeight,
  text,
  bold,
  italic,
  upperCase,
}: UseTextLineHeightPxArgs): TextLineMetrics | null => {
  const fontsSignal = useFontsLoadedSignal();

  return useMemo(() => {
    // Read the signal so the measurement re-runs whenever a webfont finishes
    // loading (otherwise we'd keep the fallback-metric result).
    void fontsSignal;

    const ctx = getMeasureContext();
    if (!ctx || !fontSizePx || !fontFamily) return null;

    // CSS font shorthand: style weight size family.
    const cssFont = `${italic ? "italic " : ""}${bold ? "bold " : ""}${fontSizePx}px "${fontFamily}"`;
    // Bail until the exact font is available — otherwise we'd measure a
    // fallback and the line-height would jump when the real font loads.
    if (
      typeof document.fonts.check === "function" &&
      !document.fonts.check(cssFont)
    ) {
      return null;
    }

    ctx.font = cssFont;
    const content = upperCase ? text.toUpperCase() : text;
    const lines = content.split("\n");

    // Ink box (per the renderer's PIL getbbox) → drives the line-height.
    let inkHeight = 0;
    // Font metrics box (what CSS uses for the line-box content area), font-level.
    let metricsAscent = 0;
    let metricsDescent = 0;
    for (const line of lines) {
      const metrics = ctx.measureText(line.length ? line : " ");
      inkHeight = Math.max(
        inkHeight,
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
      );
      metricsAscent = Math.max(metricsAscent, metrics.fontBoundingBoxAscent);
      metricsDescent = Math.max(metricsDescent, metrics.fontBoundingBoxDescent);
    }

    if (!inkHeight || !Number.isFinite(inkHeight)) return null;

    // Per-line ink extents for the outer edges (renderer aligns the first
    // line's ink-top to the box top, the last line's ink-bottom to the bottom).
    const firstInkAscent = ctx.measureText(
      lines[0]?.length ? lines[0] : " "
    ).actualBoundingBoxAscent;
    const lastLine = lines[lines.length - 1];
    const lastInkDescent = ctx.measureText(
      lastLine?.length ? lastLine : " "
    ).actualBoundingBoxDescent;

    const lineHeightPx = inkHeight * (1 + lineHeight);
    // Empty space CSS leaves above/below the text within the line box:
    //   half-leading (line-height extra) + (font ascent/descent − ink ascent/descent)
    const halfLeading = (lineHeightPx - (metricsAscent + metricsDescent)) / 2;
    return {
      lineHeightPx,
      topInsetPx: halfLeading + (metricsAscent - firstInkAscent),
      bottomInsetPx: halfLeading + (metricsDescent - lastInkDescent),
    };
  }, [
    fontFamily,
    fontSizePx,
    lineHeight,
    text,
    bold,
    italic,
    upperCase,
    fontsSignal,
  ]);
};
